import { VexRoutes } from '@pharvio/core/ui';
import {
  authGuard,
  canDeactivateGuard,
  forcedPasswordChangeGuard,
  permissionGuard,
  roleGuard
} from './core/guards';
import { PERMISSIONS } from './core/constants/permissions.constant';
import { ORDER_ROUTES } from './core/constants/routes/orders-routes';
import { LayoutComponent } from './layouts/layout/layout.component';
import { APP_ROUTES } from './app-routes';
import { DashboardComponent } from './features/dashboard/containers/dashboard/dashboard.component';
import { CreateOrderComponent } from './features/orders/containers/create-order/create-order.component';
import { OrderDetailComponent } from './features/orders/containers/order-detail/order-detail.component';
import { OrdersListComponent } from './features/orders/containers/orders-list/orders-list.component';
import { PosShellComponent } from './features/pos/containers/pos-shell/pos-shell.component';
import {
  OFFLINE_ROUTE_DATA_KEY,
  offlineRouteChildGuard,
  OfflineUnavailablePageComponent
} from '@pharvio/offline';

/** Route data marking a route (and its children) as usable while offline. */
const OFFLINE_AVAILABLE = { [OFFLINE_ROUTE_DATA_KEY]: true };

export const appRoutes: VexRoutes = [
  // Auth screens (no layout, no auth required)
  {
    path: APP_ROUTES.Auth,
    loadChildren: () =>
      import('./features/auth/auth.routes').then((m) => m.authRoutes)
  },

  // Permission denied (no layout needed)
  {
    path: APP_ROUTES.PermissionDenied,
    loadComponent: () =>
      import(
        './shared/components/permission-denied/permission-denied.component'
      ).then((m) => m.PermissionDeniedComponent)
  },

  // All protected routes inside the Vex shell layout
  {
    path: '',
    component: LayoutComponent,
    canActivate: [authGuard, forcedPasswordChangeGuard],
    // // Re-check on every in-shell navigation so a forced password change
    // // can't be sidestepped by navigating within the layout.
    // canActivateChild: [forcedPasswordChangeGuard],
    // While offline, only routes flagged OFFLINE_AVAILABLE are reachable.
    canActivateChild: [offlineRouteChildGuard],
    children: [
      // Dashboard (home)
      {
        path: APP_ROUTES.Dashboard,
        data: OFFLINE_AVAILABLE,
        component: DashboardComponent
      },

      // Shown when a page that needs a connection is opened offline
      {
        path: APP_ROUTES.OfflineUnavailable,
        data: OFFLINE_AVAILABLE,
        // The offline lib is already in the main bundle (guard + boot), so a
        // lazy import would buy nothing and the page must work with no network.
        component: OfflineUnavailablePageComponent
      },

      // Catalog
      {
        path: APP_ROUTES.Catalog,
        loadChildren: () =>
          import('./features/catalog/catalog.routes').then(
            (m) => m.catalogRoutes
          )
      },

      // Users & Roles
      {
        path: APP_ROUTES.Users,
        loadChildren: () =>
          import('./features/users/users.routes').then((m) => m.usersRoutes)
      },
      {
        path: APP_ROUTES.Roles,
        loadChildren: () =>
          import('./features/users/users.routes').then((m) => m.rolesRoutes)
      },

      // Profile
      {
        path: APP_ROUTES.Profile,
        data: OFFLINE_AVAILABLE,
        loadComponent: () =>
          import('./features/users/containers/profile/profile.component').then(
            (m) => m.ProfileComponent
          )
      },
      {
        path: 'profile/credentials',
        loadComponent: () =>
          import(
            './features/users/containers/profile-credentials/profile-credentials.component'
          ).then((m) => m.ProfileCredentialsComponent),
        canActivate: [roleGuard],
        data: { roles: ['pharmacist'] }
      },

      // POS & Sales
      {
        path: APP_ROUTES.POS,
        data: OFFLINE_AVAILABLE,
        canDeactivate: [canDeactivateGuard],
        component: PosShellComponent
      },
      {
        path: APP_ROUTES.Invoices,
        loadChildren: () =>
          import('./features/pos/pos.routes').then((m) => m.invoicesRoutes)
      },

      // Prescriptions
      {
        path: APP_ROUTES.Prescriptions,
        loadChildren: () =>
          import('./features/prescriptions/prescriptions.routes').then(
            (m) => m.prescriptionsRoutes
          )
      },

      // Returns
      {
        path: APP_ROUTES.Returns,
        loadChildren: () =>
          import('./features/returns/returns.routes').then(
            (m) => m.returnsRoutes
          )
      },

      // Inventory
      {
        path: APP_ROUTES.Inventory,
        loadChildren: () =>
          import('./features/inventory/inventory.routes').then(
            (m) => m.inventoryRoutes
          )
      },

      // Customers
      {
        path: APP_ROUTES.Customers,
        loadChildren: () =>
          import('./features/customers/customers.routes').then(
            (m) => m.customersRoutes
          )
      },

      // Online Orders
      {
        path: APP_ROUTES.Orders,
        data: OFFLINE_AVAILABLE,
        children: [
          {
            path: ORDER_ROUTES.Create,
            canActivate: [permissionGuard],
            data: { permissions: [PERMISSIONS.ORDERS.CREATE] },
            component: CreateOrderComponent
          },
          {
            path: ORDER_ROUTES.Detail,
            component: OrderDetailComponent
          },
          {
            path: ORDER_ROUTES.List,
            component: OrdersListComponent
          }
        ]
      },

      // Financial Reports
      {
        path: APP_ROUTES.Reports,
        loadChildren: () =>
          import('./features/reports/reports.routes').then(
            (m) => m.reportsRoutes
          )
      },

      // Loyalty
      {
        path: APP_ROUTES.Loyalty,
        loadChildren: () =>
          import('./features/loyalty/loyalty.routes').then(
            (m) => m.loyaltyRoutes
          )
      },

      // AI Assistant
      {
        path: APP_ROUTES.Assistant,
        loadChildren: () =>
          import('./features/assistant/assistant.routes').then(
            (m) => m.assistantRoutes
          )
      },

      // Bookings
      {
        path: APP_ROUTES.Bookings,
        canActivate: [permissionGuard],
        data: { permissions: [PERMISSIONS.BOOKINGS.READ] },
        loadChildren: () =>
          import('./features/bookings/bookings.routes').then(
            (m) => m.bookingsRoutes
          )
      },

      // Bookable services (the catalogue behind the bookings)
      {
        path: APP_ROUTES.BookableServices,
        canActivate: [permissionGuard],
        data: { permissions: [PERMISSIONS.BOOKABLE_SERVICES.READ] },
        loadComponent: () =>
          import(
            './features/bookings/containers/bookable-services-list/bookable-services-list.component'
          ).then((m) => m.BookableServicesListComponent)
      },

      // Expenses
      {
        path: APP_ROUTES.Expenses,
        canActivate: [permissionGuard],
        data: { permissions: [PERMISSIONS.EXPENSES.READ] },
        loadChildren: () =>
          import('./features/expenses/expenses.routes').then(
            (m) => m.expensesRoutes
          )
      },

      // Expense Categories
      {
        path: APP_ROUTES.ExpenseCategories,
        canActivate: [permissionGuard],
        canDeactivate: [canDeactivateGuard],
        data: { permissions: [PERMISSIONS.EXPENSES.READ] },
        loadComponent: () =>
          import(
            './features/expenses/containers/expense-categories-settings/expense-categories-settings.component'
          ).then((m) => m.ExpenseCategoriesSettingsComponent)
      },

      // Tax Classes
      {
        path: APP_ROUTES.TaxRules,
        canActivate: [permissionGuard],
        canDeactivate: [canDeactivateGuard],
        data: { permissions: [PERMISSIONS.SETTINGS.TAX_CLASSES_READ] },
        loadComponent: () =>
          import(
            './features/settings/containers/tax-rules-settings/tax-rules-settings.component'
          ).then((m) => m.TaxRulesSettingsComponent)
      },

      // Payout Accounts
      {
        path: APP_ROUTES.Payouts,
        loadComponent: () =>
          import(
            './features/settings/containers/payout-accounts-settings/payout-accounts-settings.component'
          ).then((m) => m.PayoutAccountsSettingsComponent)
      },

      // Settings
      {
        path: APP_ROUTES.Settings,
        loadChildren: () =>
          import('./features/settings/settings.routes').then(
            (m) => m.settingsRoutes
          )
      },

      // Refund Tickets
      {
        path: APP_ROUTES.RefundTickets,
        loadChildren: () =>
          import('./features/refund-tickets/refund-tickets.routes').then(
            (m) => m.refundTicketsRoutes
          )
      },

      // Shifts
      {
        path: APP_ROUTES.Shifts,
        loadChildren: () =>
          import('./features/shifts/shifts.routes').then((m) => m.shiftsRoutes)
      },

      {
        path: APP_ROUTES.Branches,
        loadComponent: () =>
          import(
            './features/branches/containers/branches-list/branches-list.component'
          ).then((m) => m.BranchesListComponent)
      },
      {
        path: 'branches/:branchId/superintendents',
        loadComponent: () =>
          import(
            './features/branches/containers/branch-superintendents/branch-superintendents.component'
          ).then((m) => m.BranchSuperintendentsComponent)
      },
      {
        path: APP_ROUTES.AuditLog,
        loadComponent: () =>
          import(
            './features/audit-log/containers/audit-log-list/audit-log-list.component'
          ).then((m) => m.AuditLogListComponent)
      },
      {
        path: APP_ROUTES.Storefront,
        loadChildren: () =>
          import('./features/storefront/storefront.routes').then(
            (m) => m.storefrontRoutes
          )
      },
      {
        path: APP_ROUTES.Subscription,
        loadComponent: () =>
          import(
            './features/subscription/containers/subscription-page/subscription-page.component'
          ).then((m) => m.SubscriptionPageComponent)
      }
    ]
  },

  { path: '**', redirectTo: '' }
];
